<template>
  <section class="project-details" v-if="project">
    <div class="container">
      <h2>{{ project.title }}</h2>

      <!-- Galerie moderne -->
      <div class="images-gallery" role="list">
        <button v-for="(img, index) in project.images" :key="index" class="gallery-tile" @click="openLightbox(index)"
          :aria-label="`Agrandir l’image ${index + 1}`" role="listitem">
          <img :src="img" :alt="`${project.title} ${index + 1}`" loading="lazy" decoding="async" />
        </button>
      </div>

      <p v-html="linkify(project.description)"></p>

      <button class="btn-back" @click="goBack">← Retour</button>
    </div>

    <!-- Lightbox -->
    <div v-if="lightbox.open" class="lightbox" @click.self="closeLightbox">
      <button class="lb-btn lb-close" @click="closeLightbox" aria-label="Fermer">✕</button>
      <button class="lb-btn lb-prev" @click.stop="prevImage" aria-label="Image précédente">‹</button>
      <figure class="lb-stage">
        <img :src="project.images[lightbox.index]" :alt="`${project.title} — image ${lightbox.index + 1}`"
          ref="lbImg" />
        <figcaption class="lb-caption">
          {{ lightbox.index + 1 }} / {{ project.images.length }}
        </figcaption>
      </figure>
      <button class="lb-btn lb-next" @click.stop="nextImage" aria-label="Image suivante">›</button>
    </div>
  </section>

  <section v-else class="not-found">
    <h2>Projet introuvable</h2>
    <button class="btn-back" @click="goBack">← Retour</button>
  </section>
</template>

<script>
import { projects } from "@/data/projectsData.js";

export default {
  name: "ProjectDetails",
  props: ["slug"],
  data() {
    return {
      lightbox: { open: false, index: 0 },
    };
  },
  computed: {
    project() {
      return projects.find((p) => p.slug === this.slug);
    },
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push("/projects");
    },
    linkify(texte) {
      if (!texte) return "";
      const regex = /https?:\/\/[^\s)]+/g;
      return texte.replace(regex, (url) => {
        const safe = url.replace(/"/g, "&quot;");
        return `<a 
                  href="${safe}" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style="color:#67cada; text-decoration:underline; word-break:break-word;"
                >${url}</a>`;
      });
    },
    openLightbox(i) {
      this.lightbox.index = i;
      this.lightbox.open = true;
      document.addEventListener("keydown", this.onKeydown, { passive: true });
    },
    closeLightbox() {
      this.lightbox.open = false;
      document.removeEventListener("keydown", this.onKeydown);
    },
    nextImage() {
      const n = this.project.images.length;
      this.lightbox.index = (this.lightbox.index + 1) % n;
    },
    prevImage() {
      const n = this.project.images.length;
      this.lightbox.index = (this.lightbox.index - 1 + n) % n;
    },
    onKeydown(e) {
      if (!this.lightbox.open) return;
      if (e.key === "Escape") this.closeLightbox();
      else if (e.key === "ArrowRight") this.nextImage();
      else if (e.key === "ArrowLeft") this.prevImage();
    },
  },
  mounted() {
    // sécurité supplémentaire si un style tiers a forcé un défilement
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  },
};
</script>

<style scoped>
.project-details {
  text-align: center;
  padding: 50px 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 70px;
}

.project-details h2 {
  font-size: 2rem;
  margin-bottom: 24px;
  color: #0f172a;
}

/* =========================
   GALERIE — grilles propres
========================= */
.images-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  justify-items: stretch;
  align-items: stretch;
  margin-bottom: 26px;
}

.gallery-tile {
  position: relative;
  aspect-ratio: 4 / 3;
  /* ratio constant = mosaïque soignée */
  border: 1px solid #e8eef2;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f7fb;
  padding: 0;
  cursor: zoom-in;
  display: block;
  box-shadow: 0 4px 14px rgba(2, 12, 27, 0.06);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.gallery-tile:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 202, 218, .45);
  box-shadow: 0 12px 28px rgba(103, 202, 218, .18);
}

.gallery-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* cadrage élégant */
  display: block;
  backface-visibility: hidden;
  transform: translateZ(0);
  transition: transform .35s ease, filter .35s ease;
}

.gallery-tile:hover img {
  transform: scale(1.03);
  filter: saturate(1.05);
}

.project-details p {
  font-size: 1.06rem;
  margin-bottom: 25px;
  color: #475569;
  line-height: 1.65;
}

/* =========================
   BOUTON RETOUR
========================= */
.btn-back {
  background: #67cada;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  color: white;
  font-weight: 800;
  cursor: pointer;
  transition: background .2s, transform .15s, box-shadow .2s;
  box-shadow: 0 10px 24px rgba(103, 202, 218, .28);
}

.btn-back:hover {
  background: #50b5c4;
  transform: translateY(-1px);
}

/* =========================
   LIGHTBOX — plein écran
========================= */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 23, 0.86);
  backdrop-filter: blur(2px);
  display: grid;
  grid-template-columns: 64px 1fr 64px;
  grid-template-rows: 1fr;
  align-items: center;
  justify-items: center;
  z-index: 9999;
}

.lb-stage {
  margin: 0;
  max-width: min(92vw, 1400px);
  max-height: 86vh;
  display: grid;
  align-items: center;
  justify-items: center;
  gap: 10px;
}

.lb-stage img {
  max-width: 100%;
  max-height: 86vh;
  object-fit: contain;
  /* ne coupe jamais l’image */
  border-radius: 14px;
  background: #0b0d12;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .45);
}

.lb-caption {
  color: #e6e8ec;
  font-size: .95rem;
  text-align: center;
  opacity: .9;
}

.lb-btn {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .22);
  background: rgba(15, 17, 21, .7);
  color: #fff;
  font-size: 22px;
  line-height: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform .15s, box-shadow .2s, background .2s;
  user-select: none;
}

.lb-btn:hover {
  transform: scale(1.06);
  background: rgba(15, 17, 21, .9);
  box-shadow: 0 10px 26px rgba(0, 0, 0, .35);
}

.lb-prev {
  grid-column: 1;
}

.lb-next {
  grid-column: 3;
}

.lb-close {
  position: absolute;
  top: 18px;
  right: 18px;
  font-size: 18px;
  border-color: rgba(255, 255, 255, .28);
}

/* Responsive */
@media (max-width: 768px) {
  .images-gallery {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
  }

  .lightbox {
    grid-template-columns: 56px 1fr 56px;
  }
}
</style>
